// src/hooks/useProjectSections.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from '@/hooks/useStableAuth';
import { toast } from 'sonner';
import type { ProjectSection } from '@/types/project';

export const projectSectionKeys = {
  all: ['project-sections'] as const,
  byProject: (projectId: string) =>
    [...projectSectionKeys.all, projectId] as const,
};

export function useProjectSections(projectId: string | undefined) {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  const invalidate = () => {
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: projectSectionKeys.byProject(projectId),
      });
    }
  };

  // ── Listagem de seções ordenadas ───────────────────────────────────────────
  const sectionsQuery = useQuery({
    queryKey: projectSectionKeys.byProject(projectId ?? ''),
    queryFn: async (): Promise<ProjectSection[]> => {
      const { data, error } = await supabase
        .from('project_sections')
        .select('*')
        .eq('project_id', projectId!)
        .order('position', { ascending: true });

      if (error) throw error;
      return (data ?? []) as ProjectSection[];
    },
    enabled: !!projectId && !!userId && !!companyId,
  });

  // ── Criar seção ────────────────────────────────────────────────────────────
  const createSectionMutation = useMutation({
    mutationFn: async ({
      name,
    }: {
      name: string;
    }): Promise<void> => {
      // Posição = MAX(position) + 1000 para espaçamento
      const maxPosition =
        sectionsQuery.data?.reduce(
          (max, s) => Math.max(max, s.position),
          0
        ) ?? 0;

      const { data: profileData } = await supabase.auth.getUser();
      const createdBy = profileData?.user?.id;

      const { error } = await supabase.from('project_sections').insert({
        project_id: projectId!,
        name,
        position: maxPosition + 1000,
        company_id: companyId!,
        created_by: createdBy!,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Seção criada.');
    },
    onError: (error: Error) => {
      console.error('Erro ao criar seção:', error);
      toast.error('Erro ao criar seção.');
    },
  });

  // ── Renomear seção ─────────────────────────────────────────────────────────
  const renameSectionMutation = useMutation({
    mutationFn: async ({
      sectionId,
      name,
    }: {
      sectionId: string;
      name: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from('project_sections')
        .update({ name })
        .eq('id', sectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (error: Error) => {
      console.error('Erro ao renomear seção:', error);
      toast.error('Erro ao renomear seção.');
    },
  });

  // ── Deletar seção ──────────────────────────────────────────────────────────
  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string): Promise<void> => {
      const { error } = await supabase
        .from('project_sections')
        .delete()
        .eq('id', sectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Seção removida.');
    },
    onError: (error: Error) => {
      console.error('Erro ao deletar seção:', error);
      toast.error('Erro ao deletar seção.');
    },
  });

  return {
    sections: sectionsQuery.data ?? [],
    isLoading: sectionsQuery.isLoading,
    createSection: createSectionMutation.mutateAsync,
    isCreatingSection: createSectionMutation.isPending,
    renameSection: renameSectionMutation.mutateAsync,
    deleteSection: deleteSectionMutation.mutateAsync,
    isDeletingSection: deleteSectionMutation.isPending,
  };
}
